import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { ConstantContactContact } from '../constant-contact-contact';
import { ConstantContactContactPostRequest } from '../constant-contact-contact-post-request';
import { ConstantContactContactPutRequest } from '../constant-contact-contact-put-request';
import { ConstantContactCustomField } from '../constant-contact-custom-field';
import { ConstantContactEmailAddressPost } from '../constant-contact-email-address-post';
import { ConstantContactList } from '../constant-contact-list';
import { PermissionToSend } from '../constant-contact-permission-to-send';
import { ConstantContactStreetAddress } from '../constant-contact-street-address';
import { ContactRequest } from '../contact-request';
import { EmailSignature } from '../email-signature';
import { HttpService } from '../service/http.service';
import { Language } from '../language';
import { LeadStatus } from '../lead-status';
import { LeadType } from '../lead-type';
import { ProgrammeInformation } from '../programme-information';
import { SalesAgent } from '../sales-agent';
import { ToastMessageService } from '../toast-message.service';
import { ConfirmationService } from 'primeng/api';
import { ConstantContactStreetAddressPut } from '../constant-contact-street-address-put';
import { ValidatorConstants } from 'src/global/validator-constants';
import { SystemUser } from '../system-user';
import { UserForBackend } from '../user-for-backend';
import { SimpleSchoolWithCountries } from '../simple-school-with-countries';
import { Country } from '../country';

@Injectable({
  providedIn: 'root'
})
export class ContactRequestService {

  public programmeInformationForm!: FormArray;

  public requestForm!: FormGroup;
  public counsellors: UserForBackend[] = [];
  public salesAgents: SalesAgent[] = [];

  public contactRequest!: ContactRequest | null;
  public leadType!: LeadType;
  public leadStatuses: LeadStatus[] = [];
  public display = false;
  public isCreatingConstantContactContact = false;

  public selectedConstantContactContact!: ConstantContactContact;
  public constantContactPostRequest!: ConstantContactContactPostRequest;

  public targetList: ConstantContactList[] = [];
  public contactLists: ConstantContactList[] = [];

  selectedContact: ConstantContactContact | null = null;
  sendingConstantContact = false;
  selectedContactList: ConstantContactList | null = null;
  listBoxDisabled = false;
  isUnsubscribed = false;
  emailSignatures: EmailSignature[] = [];

  contactRequestForm!: FormGroup;
  lviSiteUrl: string | null = null;
  programmeInformations: ProgrammeInformation[] = [];

  languages: Language[] = [];
  constantContactErrorMessage!: string | null;
  loadingConstantContact = false;
  isSendingRequest = false;

  schools: SimpleSchoolWithCountries[] = [];
  filteredSchools: SimpleSchoolWithCountries[] = [];
  countries: Country[] = [];
  filteredCountries: Country[] = [];

  constructor(
    private httpService: HttpService,
    private toastService: ToastMessageService,
    private builder: FormBuilder,
    private confirmationService: ConfirmationService,
    private router: Router
  ) {
    this.httpService.getLanguages().subscribe(languages => {
      this.languages = languages;
    });

  }

  initProgrammeInformationForm() {
    this.programmeInformationForm = this.builder.array([]);
    for (let language of this.languages) {
      this.programmeInformationForm.controls.push(this.builder.group({
        url: [null, Validators.required],
        language: language,
        id: null,
        name: null
      }));
    }
    this.httpService.getProgrammeInformationsBySchoolId(this.contactRequestForm.value.school.id).subscribe(res => {
      for(let info of res){
        for(let form of this.programmeInformationForm.controls){
          if(form.value.language.id == info.language.id){
            form.get("url")!.setValue(info.lviSiteUrl);
            form.get("id")!.setValue(info.id);
            form.get("name")!.setValue(info.name);
          }
        }
      }
    });
  }

  public updateAgent(agent: SalesAgent) {
    this.contactRequest!.salesAgent = agent;
    this.httpService.updateContactRequest(this.contactRequest!).subscribe(() => {
      this.toastService.addSuccess("Success", "Agent updated");
    }, error => this.toastService.addError("Error", error.message));
  }

  public initRequestForm() {
    this.requestForm = this.builder.group({
      usersInCharge: this.builder.array([]),
      salesAgent: {value: this.contactRequest?.salesAgent, disabled: this.contactRequest!.isDeleted},
      processStatus: { value: this.contactRequest!.isProcessed, disabled: this.contactRequest!.isDeleted },
      leadStatus: { value: this.contactRequest!.leadStatus, disabled: this.contactRequest!.isDeleted }
    });
  }

  public updateLeadStatus(status: LeadStatus) {
    this.contactRequest!.leadStatus = status;
    this.httpService.updateLeadStatus(this.contactRequest!.id!, status).subscribe(() => {
      this.toastService.addSuccess("Success!", "Lead status changed to " + status.name);
    }, error => this.toastService.addError("Error", error.message));
  }

  checkPhoneNumber(){
    if(this.contactRequest!.phoneNumber==null){
      return true;
    }
    let regex = new RegExp(ValidatorConstants.PHONE_NUMBER_REGEX);
    if(regex.test(this.contactRequest!.phoneNumber)){
      return true;
    } else {
      return false;
    }
  }

  public activateConstantContactDialog() {
    this.constantContactErrorMessage = null;
    this.httpService.getConstantContactLists().subscribe(res => {
      this.contactLists = res.lists.sort((a,b) => {
        if(a.name.toLowerCase() > b.name.toLowerCase()){
          return 1;
        } else {
          return -1;
        }
      });

      this.display = true;
      this.isUnsubscribed = false;
      if (this.contactRequest!.email) {
        this.loadingConstantContact = true;
        this.httpService.getConstantContactContactListByEmail(this.contactRequest!.email).subscribe(res => {
          this.loadingConstantContact = false;
          if (res.contacts.length > 0) {
            this.isCreatingConstantContactContact = false;
            this.selectedConstantContactContact = res.contacts[0];
            if (this.selectedConstantContactContact.emailAddress.permissionToSend == PermissionToSend.UNSUBSCRIBED) {
              this.isUnsubscribed = true;
            } else if (this.selectedConstantContactContact.listMemberships && this.selectedConstantContactContact.listMemberships.length > 0) {
              this.initTargetList();
            }
          }
          else {
            this.isCreatingConstantContactContact = true;
            this.constantContactPostRequest = this.getNewConstantContactContact();
          }
        });
      } else {
        return;
      }
    }, error => {
      this.loadingConstantContact = false;
      this.constantContactErrorMessage = "An error occurred in getting contact list. Please contact system admin:  " + error.error.message
    });

  }

  closeModal() {
    this.display = false;
    this.selectedContactList = null;
  }

  private initTargetList() {
    this.targetList = [];
    console.log("selectedContact.listmemberships");
    console.log(this.selectedConstantContactContact.listMemberships);
    for (let listId of this.selectedConstantContactContact.listMemberships) {
      for (let list of this.contactLists) {
        if (list.listId == listId) {
          this.targetList.push(list);
        }
      }
    }
  }

  private getNewConstantContactContact() {
    return new ConstantContactContactPostRequest(
      new ConstantContactEmailAddressPost(this.contactRequest!.email, null),
      this.contactRequest!.firstName,
      this.contactRequest!.lastName,
      null,
      null,
      "Account",
      null,
      null,
      this.contactRequest!.birthDate == null ? null : moment(this.contactRequest!.birthDate).format("YYYY-MM-DD"),
      [],
      [],
      [new ConstantContactStreetAddressPut("home", null, this.contactRequest!.city, null, this.contactRequest!.postCode, this.contactRequest!.isoCode)],
      [],
      []
    );
  }

  removeConstantContactList(selectedListId: ConstantContactList) {
    this.targetList = this.targetList.filter(val => val != selectedListId);
    this.updateConstantContact();
  }

  selectConstantContactList(value: ConstantContactList) {
    this.listBoxDisabled = true;
    for (let list of this.targetList) {
      if (list.listId == value.listId) {
        this.listBoxDisabled = false;
        return;
      }
    }
    this.targetList.push(value);
    if (this.isCreatingConstantContactContact) {
      this.addConstantContact();
    } else {
      this.updateConstantContact();
    }
    this.listBoxDisabled = false;
  }


  updateConstantContact() {
    let newMemberships = [];
    for (let list of this.targetList) {
      newMemberships.push(list.listId);
    }
    let putRequest = new ConstantContactContactPutRequest(
      this.selectedConstantContactContact.emailAddress,
      this.selectedConstantContactContact.firstName,
      this.selectedConstantContactContact.lastName,
      this.selectedConstantContactContact.companyName,
      this.selectedConstantContactContact.birthdayMonth,
      this.selectedConstantContactContact.birthdayDay,
      this.selectedConstantContactContact.anniversary,
      "Account",
      this.selectedConstantContactContact.customFields,
      this.selectedConstantContactContact.phoneNumbers,
      this.selectedConstantContactContact.streetAddresses,
      newMemberships,
      this.selectedConstantContactContact.taggings
    );

    this.httpService.putConstantContactContact(putRequest, this.selectedConstantContactContact.contactId).subscribe(res => {
      this.toastService.addSuccess("Success", "Contact updated");
    }, error => {
      this.toastService.addError("Error", error.message);
      console.error(error);
    });
  }

  addConstantContact() {
    this.sendingConstantContact = true;
    if (this.contactRequest!.birthDate) {
      this.constantContactPostRequest.customFields = [
        new ConstantContactCustomField("add5fa5c-d413-11eb-97c2-fa163e504c74", moment(this.contactRequest!.birthDate).year().toString())
      ];
    }

    this.httpService.postConstantContactContact(this.constantContactPostRequest).subscribe(res => {
      this.display = false;
      this.selectedContact = null;
      this.sendingConstantContact = false;
      this.toastService.addSuccess("Success", "Contact is posted successfully");
    }, error => {
      this.sendingConstantContact = false;
      this.toastService.addError("Error", error.message);
    });
  }

  deleteContact() {
    this.confirmationService.confirm({
      message: "Are you sure that you want to delete this record?",
      accept: () => {
        this.httpService.deleteContactRequestById(this.contactRequest!.id!).subscribe(() => {
          this.router.navigateByUrl("/management/leads");
        });
      }
    });
  }

  getCheckedUsers() {
    let newUsers: UserForBackend[] = [];
    for (let j = 0; j < (this.requestForm.controls["usersInCharge"] as FormArray).length; j++) {
      if ((this.requestForm.controls["usersInCharge"] as FormArray).controls[j].value == true) {
        newUsers.push(this.counsellors[j]);
      }
    }
    return newUsers;
  }

  updateRequest() {
    this.contactRequest!.usersInCharge = this.getCheckedUsers();
    this.httpService.updateContactRequestUsersInCharge(this.contactRequest!.id!, this.getCheckedUsers()).subscribe(() => {
      this.toastService.addSuccess("Success", "Updated");
    }, error => {
      this.toastService.addError("Error", error.message);
    });
  }

  setCounsellors() {
    for (let counsellor of this.counsellors) {
      let selected = false;
      for (let user of this.contactRequest!.usersInCharge!) {
        if (counsellor.id == user.id) {
          selected = true;
        }
      }
      (this.requestForm.controls["usersInCharge"] as FormArray).push(new FormControl({value: selected, disabled: this.contactRequest!.isDeleted}));
    }
  }
}
